import { useState } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Alert,
  Loader,
  Title,
  Divider,
  Card,
  ActionIcon,
  Modal,
  NumberInput,
  TextInput,
  Textarea,
  Select
} from '@mantine/core';
import {
  IconRefresh,
  IconCheck,
  IconX,
  IconEdit,
  IconAlertCircle,
  IconRosetteDiscountCheck
} from '@tabler/icons-react';
import { useApi, useLiveQuery, useApiClient, useSession } from '../../api';

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const formatUsd = (cents) => (cents == null ? '' : usd.format(cents / 100));

const SpecialRequestsList = ({ isPrincess = false }) => {
  const api = useApiClient();
  const { me } = useSession();

  // The princess-side topic needs the seller_profile id (contract §6.3);
  // SessionMe does not carry it, so fetch the own seller profile.
  const { data: sellerProfile } = useApi('/api/v1/me/seller', undefined, {
    enabled: isPrincess,
  });

  // Live request list: requester side listens on user.{clerk}.requests,
  // princess side on seller.{seller_profile_id}.requests. Every lifecycle
  // event (created/accepted/rejected/countered/counter_accepted/cancelled)
  // invalidates the list.
  const topic = isPrincess
    ? sellerProfile && `seller.${sellerProfile.id}.requests`
    : me && `user.${me.clerk_user_id}.requests`;

  const { data, loading, error: fetchError, refetch } = useLiveQuery(
    '/api/v1/requests',
    {},
    { topics: [topic] }
  );
  const requests = data?.items || [];

  const [error, setError] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [counterOfferModal, setCounterOfferModal] = useState(false);
  // Form state keeps the price in dollars; converted to integer cents on submit
  const [counterOfferData, setCounterOfferData] = useState({});

  const runAction = async (requestId, action, fallbackMessage) => {
    try {
      setError(null);
      await api.post(`/api/v1/requests/${requestId}/${action}`);
      refetch();
    } catch (err) {
      setError(err?.message || fallbackMessage);
    }
  };

  const handleAccept = (requestId) => runAction(requestId, 'accept', 'Failed to accept request');
  const handleReject = (requestId) => runAction(requestId, 'reject', 'Failed to reject request');
  const handleAcceptCounterOffer = (requestId) =>
    runAction(requestId, 'accept-counter', 'Failed to accept counter offer');
  // The requester cannot reject — declining a counter offer means cancelling
  // the request (POST /requests/{id}/cancel is the requester-only action).
  const handleCancel = (requestId) => runAction(requestId, 'cancel', 'Failed to cancel request');
  // Contract §4.6: princess marks an accepted request completed (400 unless accepted)
  const handleComplete = (requestId) =>
    runAction(requestId, 'complete', 'Failed to mark request as completed');

  const handleCounterOffer = async () => {
    try {
      setError(null);
      // Contract §4.6: body fields have NO counter_ prefix; money is integer cents
      const body = {
        nail_polish_color: counterOfferData.nail_polish_color || null,
        design: counterOfferData.design || null,
        service_type: counterOfferData.service_type || null,
        special_notes: counterOfferData.special_notes || null,
        number_of_pictures: counterOfferData.number_of_pictures ?? null,
        offer_price_cents:
          counterOfferData.offer_price_dollars != null && counterOfferData.offer_price_dollars !== ''
            ? Math.round(Number(counterOfferData.offer_price_dollars) * 100)
            : null,
      };
      // Only send the fields the princess actually set (at least one required)
      const payload = Object.fromEntries(
        Object.entries(body).filter(([, v]) => v !== null)
      );
      await api.post(`/api/v1/requests/${selectedRequest.id}/counter-offer`, payload);
      setCounterOfferModal(false);
      setSelectedRequest(null);
      setCounterOfferData({});
      refetch();
    } catch (err) {
      setError(err?.message || 'Failed to submit counter offer');
    }
  };

  const openCounterOfferModal = (request) => {
    setSelectedRequest(request);
    setCounterOfferData({
      nail_polish_color: request.nail_polish_color || '',
      design: request.design || '',
      service_type: request.service_type,
      special_notes: request.special_notes || '',
      number_of_pictures: request.number_of_pictures,
      offer_price_dollars: request.offer_price_cents != null ? request.offer_price_cents / 100 : 50,
    });
    setCounterOfferModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'yellow',
      accepted: 'green',
      counter_offered: 'pink',
      rejected: 'red',
      completed: 'teal',
      cancelled: 'gray'
    };
    return colors[status] || 'gray';
  };

  const getStatusLabel = (status) =>
    status === 'completed' ? 'COMPLETED ✓' : status.replace('_', ' ').toUpperCase();

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>Special Requests</Title>
        <ActionIcon onClick={refetch} variant="light">
          <IconRefresh size="1.2rem" />
        </ActionIcon>
      </Group>

      {(error || fetchError) && (
        <Alert icon={<IconAlertCircle size="1rem" />} color="red">
          {error || fetchError?.message}
        </Alert>
      )}

      {requests.length === 0 ? (
        <Paper p="xl" withBorder>
          <Text c="dimmed" ta="center">
            No special requests yet
          </Text>
        </Paper>
      ) : (
        <Stack>
          {requests.map((request) => (
            <Card key={request.id} shadow="sm" p="lg" radius="md" withBorder>
              <Stack>
                <Group justify="space-between">
                  <Group>
                    <Text fw={500}>
                      {isPrincess ? `From: ${request.requester_name}` : `To: ${request.princess_name}`}
                    </Text>
                    {isPrincess && request.requester_identity_verified && (
                      <Badge
                        color="blue"
                        variant="light"
                        leftSection={<IconRosetteDiscountCheck size="0.8rem" />}
                      >
                        Verified buyer
                      </Badge>
                    )}
                    <Badge color={getStatusColor(request.status)}>
                      {getStatusLabel(request.status)}
                    </Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {formatDate(request.created_at)}
                  </Text>
                </Group>

                <Divider />

                <Group grow>
                  <div>
                    <Text size="sm" c="dimmed">Service Type</Text>
                    <Text>{request.service_type}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Polish Color</Text>
                    <Text>{request.nail_polish_color || 'Not specified'}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Pictures</Text>
                    <Text>{request.number_of_pictures}</Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Offer Price</Text>
                    <Text fw={600} size="lg">{formatUsd(request.offer_price_cents)}</Text>
                  </div>
                </Group>

                {request.design && (
                  <div>
                    <Text size="sm" c="dimmed">Design</Text>
                    <Text>{request.design}</Text>
                  </div>
                )}

                {request.special_notes && (
                  <div>
                    <Text size="sm" c="dimmed">Special Notes</Text>
                    <Text>{request.special_notes}</Text>
                  </div>
                )}

                {request.status === 'counter_offered' && request.counter_offer && (
                  <Paper p="md" withBorder bg="pink.0">
                    <Text fw={500} mb="sm">Counter Offer</Text>
                    <Group grow>
                      {request.counter_offer.offer_price_cents != null && (
                        <div>
                          <Text size="sm" c="dimmed">New Price</Text>
                          <Text fw={600}>{formatUsd(request.counter_offer.offer_price_cents)}</Text>
                        </div>
                      )}
                      {request.counter_offer.number_of_pictures != null && (
                        <div>
                          <Text size="sm" c="dimmed">Pictures</Text>
                          <Text>{request.counter_offer.number_of_pictures}</Text>
                        </div>
                      )}
                      {request.counter_offer.service_type && (
                        <div>
                          <Text size="sm" c="dimmed">Service Type</Text>
                          <Text>{request.counter_offer.service_type}</Text>
                        </div>
                      )}
                    </Group>
                    {request.counter_offer.special_notes && (
                      <div>
                        <Text size="sm" c="dimmed" mt="sm">Counter Notes</Text>
                        <Text>{request.counter_offer.special_notes}</Text>
                      </div>
                    )}
                  </Paper>
                )}

                {isPrincess && request.status === 'pending' && (
                  <Group>
                    <Button
                      leftSection={<IconCheck size="1rem" />}
                      color="green"
                      onClick={() => handleAccept(request.id)}
                    >
                      Accept
                    </Button>
                    <Button
                      leftSection={<IconEdit size="1rem" />}
                      variant="light"
                      onClick={() => openCounterOfferModal(request)}
                    >
                      Counter Offer
                    </Button>
                    <Button
                      leftSection={<IconX size="1rem" />}
                      color="red"
                      variant="light"
                      onClick={() => handleReject(request.id)}
                    >
                      Reject
                    </Button>
                  </Group>
                )}

                {isPrincess && request.status === 'accepted' && (
                  <Group>
                    <Button
                      leftSection={<IconCheck size="1rem" />}
                      color="teal"
                      onClick={() => handleComplete(request.id)}
                    >
                      Mark Completed
                    </Button>
                  </Group>
                )}

                {!isPrincess && request.status === 'counter_offered' && (
                  <Group>
                    <Button
                      leftSection={<IconCheck size="1rem" />}
                      color="green"
                      onClick={() => handleAcceptCounterOffer(request.id)}
                    >
                      Accept Counter Offer
                    </Button>
                    <Button
                      leftSection={<IconX size="1rem" />}
                      color="red"
                      variant="light"
                      onClick={() => handleCancel(request.id)}
                    >
                      Decline & Cancel
                    </Button>
                  </Group>
                )}
              </Stack>
            </Card>
          ))}
        </Stack>
      )}

      <Modal
        opened={counterOfferModal}
        onClose={() => setCounterOfferModal(false)}
        title="Make Counter Offer"
        size="lg"
      >
        <Stack>
          <TextInput
            label="Nail Polish Color"
            value={counterOfferData.nail_polish_color || ''}
            onChange={(e) => setCounterOfferData({
              ...counterOfferData,
              nail_polish_color: e.target.value
            })}
          />

          <Textarea
            label="Design"
            value={counterOfferData.design || ''}
            onChange={(e) => setCounterOfferData({
              ...counterOfferData,
              design: e.target.value
            })}
          />

          <Select
            label="Service Type"
            data={[
              { value: 'paraffin', label: 'Paraffin' },
              { value: 'french', label: 'French' },
              { value: 'gel', label: 'Gel' },
              { value: 'athletic', label: 'Athletic' }
            ]}
            value={counterOfferData.service_type || ''}
            onChange={(value) => setCounterOfferData({
              ...counterOfferData,
              service_type: value
            })}
          />

          <Textarea
            label="Special Notes"
            value={counterOfferData.special_notes || ''}
            onChange={(e) => setCounterOfferData({
              ...counterOfferData,
              special_notes: e.target.value
            })}
          />

          <NumberInput
            label="Number of Pictures"
            min={1}
            max={20}
            value={counterOfferData.number_of_pictures || 5}
            onChange={(value) => setCounterOfferData({
              ...counterOfferData,
              number_of_pictures: value
            })}
          />

          <NumberInput
            label="Counter Offer Price ($)"
            min={10}
            max={500}
            decimalScale={2}
            value={counterOfferData.offer_price_dollars ?? 50}
            onChange={(value) => setCounterOfferData({
              ...counterOfferData,
              offer_price_dollars: value
            })}
          />

          <Group justify="flex-end">
            <Button variant="light" onClick={() => setCounterOfferModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCounterOffer}>
              Submit Counter Offer
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export default SpecialRequestsList;
